import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import {
  ChartBar,
  Package,
  Receipt,
  TrendingDown,
  TrendingUp,
} from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { ProfitSummaryDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportTermHint } from "./ReportTermHint";

type ProfitSummaryCardsProps = {
  summary?: ProfitSummaryDTO;
  isLoading?: boolean;
  collapsibleExtra?: boolean;
};

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  accentClass,
  valueClass,
}: {
  icon: typeof Receipt;
  label: ReactNode;
  value: string;
  sub?: ReactNode;
  accentClass: string;
  valueClass?: string;
}) {
  return (
    <div className="brand-card flex items-center gap-3 p-4">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span
          className={`font-display text-xl tabular-nums leading-tight sm:text-2xl ${valueClass ?? ""}`}
        >
          {value}
        </span>
        {sub ? (
          <span className="text-muted-foreground text-xs">{sub}</span>
        ) : null}
      </div>
    </div>
  );
}

export function ProfitSummaryCards({
  summary,
  isLoading,
  collapsibleExtra = true,
}: ProfitSummaryCardsProps) {
  const [showExtra, setShowExtra] = useState(false);

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {["revenue", "cogs", "gross", "margin", "expenses", "net"].map((id) => (
          <Skeleton key={`profit-card-${id}`} className="h-22 rounded-2xl" />
        ))}
      </div>
    );
  }

  const revenue = summary?.revenue ?? "0";
  const cogs = summary?.cogs ?? "0";
  const grossProfit = summary?.grossProfit ?? "0";
  const totalExpenses = summary?.totalExpenses ?? "0";
  const marketingExpenses = summary?.marketingExpenses ?? "0";
  const netProfit = summary?.netProfit ?? "0";
  const margin = summary?.grossMarginPercent ?? "0";
  const netMargin = summary?.netMarginPercent ?? "0";
  const mer = summary?.marketingEfficiencyRatio ?? "0";
  const profitNum = Number(grossProfit);
  const netProfitNum = Number(netProfit);
  const hasWithoutLot = (summary?.stockWithoutLotVariants ?? 0) > 0;
  const customJobs = summary?.customJobs;
  const mobileHideExtra = collapsibleExtra && !showExtra;

  const revenueSub = customJobs
    ? `${summary?.orderCount ?? 0} ລາຍການ (${customJobs.jobCount} ງານສັ່ງທຳ) · ບໍ່ລວມແຖມ/ຄ່າສົ່ງ`
    : `${summary?.orderCount ?? 0} ອໍເດີ້ · ບໍ່ລວມແຖມ/ຄ່າສົ່ງ`;

  return (
    <div className="flex flex-col gap-3">
      {hasWithoutLot ? (
        <div className="brand-card flex flex-col gap-1 border-destructive/25 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm">
            <span className="font-medium text-destructive">
              {summary?.stockWithoutLotQuantity ?? 0} ຊິ້ນ ບໍ່ມີ lot
            </span>
            <span className="text-muted-foreground">
              {" "}
              ({summary?.stockWithoutLotVariants ?? 0} variant) — COGS ອາດບໍ່ຖືກ
            </span>
          </p>
          <Link
            to="/app/inventory"
            className="shrink-0 text-secondary text-sm underline-offset-2 hover:underline"
          >
            ເບິ່ງ lot & ຮັບເຂົ້າ
          </Link>
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          icon={Receipt}
          label="ກຳໄລສຸດທິ"
          value={formatLak(netProfit)}
          sub={`ອັດຕາກຳໄລສຸດທິ ${netMargin}%`}
          accentClass="bg-secondary/20 text-secondary"
          valueClass={
            netProfitNum > 0
              ? "text-leaf"
              : netProfitNum < 0
                ? "text-destructive"
                : undefined
          }
        />
        <StatCard
          icon={ChartBar}
          label="ອັດຕາກຳໄລຂັ້ນຕົ້ນ"
          value={`${margin}%`}
          sub="ກຳໄລ ÷ ລາຍຮັບ"
          accentClass="bg-leaf/15 text-leaf"
        />
        <StatCard
          icon={TrendingDown}
          label="ລາຍຈ່າຍ"
          value={formatLak(totalExpenses)}
          sub={`ຄ່າແອດ ${formatLak(marketingExpenses)}`}
          accentClass="bg-destructive/10 text-destructive"
        />
        <div className={mobileHideExtra ? "hidden sm:contents" : "contents"}>
          <StatCard
            icon={TrendingUp}
            label="ລາຍຮັບ"
            value={formatLak(revenue)}
            sub={revenueSub}
            accentClass="bg-primary/20 text-secondary"
          />
          <StatCard
            icon={Package}
            label={
              <ReportTermHint
                label="ຕົ້ນທຶນຂາຍ (COGS)"
                hint="ຕົ້ນທຶນສິນຄ້າທີ່ຂາຍໄປ — ຄິດຈາກ lot ແບບ FIFO"
              />
            }
            value={formatLak(cogs)}
            sub="ຕົ້ນທຶນ FIFO ຈາກ lot"
            accentClass="bg-accent/40 text-foreground"
          />
          <StatCard
            icon={Receipt}
            label="ກຳໄລຂັ້ນຕົ້ນ"
            value={formatLak(grossProfit)}
            sub="ລາຍຮັບ − COGS"
            accentClass="bg-secondary/20 text-secondary"
            valueClass={
              profitNum > 0
                ? "text-leaf"
                : profitNum < 0
                  ? "text-destructive"
                  : undefined
            }
          />
          <StatCard
            icon={ChartBar}
            label={
              <ReportTermHint
                label="MER"
                hint="Marketing Efficiency Ratio = ລາຍຮັບ ÷ ຄ່າແອດ"
              />
            }
            value={`${mer}x`}
            sub="ລາຍຮັບ ÷ ຄ່າແອດ"
            accentClass="bg-primary/20 text-secondary"
          />
        </div>
      </div>
      {mobileHideExtra ? (
        <button
          type="button"
          onClick={() => setShowExtra(true)}
          className="self-start rounded-full border border-border bg-background px-3 py-1.5 font-medium text-muted-foreground text-xs hover:bg-muted/60 sm:hidden"
        >
          ເບິ່ງເພີ່ມ (ລາຍຮັບ, COGS, MER)
        </button>
      ) : null}
      {customJobs ? (
        <div className="brand-card grid gap-3 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-0.5 sm:col-span-2 lg:col-span-4">
            <span className="font-medium text-secondary text-xs">
              ງານສັ່ງທຳ (ລວມໃນກຳໄລແລ້ວ)
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ງານສົ່ງມອບ</span>
            <span className="font-medium tabular-nums">{customJobs.jobCount}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ລາຍຮັບ</span>
            <span className="font-medium tabular-nums">
              {formatLak(customJobs.revenue)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">COGS</span>
            <span className="font-medium tabular-nums">
              {formatLak(customJobs.cogs)}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ກຳໄລຂັ້ນຕົ້ນ</span>
            <span className="font-medium text-leaf tabular-nums">
              {formatLak(customJobs.grossProfit)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
